import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";

export default function LanguageSelector() {
  const { i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [location, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update current language when i18n language changes
  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Set initial HTML lang attribute and meta tags
  useEffect(() => {
    const currentLanguage = i18n.language;
    document.documentElement.lang = currentLanguage;
    
    // Update meta language tag for SEO
    const metaLang = document.querySelector('meta[name="language"]');
    if (metaLang) {
      const languageNames = {
        'en': 'English',
        'de': 'German', 
        'fr': 'French',
        'vi': 'Vietnamese',
        'es': 'Spanish'
      };
      metaLang.setAttribute('content', languageNames[currentLanguage as keyof typeof languageNames] || 'English');
    }
  }, [i18n.language]);

  // Beautiful SVG Flag Icons
  const FlagIcon = ({ countryCode, className = "w-6 h-4" }: { countryCode: string, className?: string }) => {
    return (
      <div className={`${className} rounded-sm overflow-hidden shadow-sm border border-gray-200`}>
        <svg className="w-full h-full" viewBox="0 0 24 16" xmlns="http://www.w3.org/2000/svg">
          {countryCode === 'US' && (
            <>
              {/* US Flag - Red stripes */}
              <rect width="24" height="16" fill="#B22234"/>
              <rect y="1.23" width="24" height="1.23" fill="#FFFFFF"/>
              <rect y="3.69" width="24" height="1.23" fill="#FFFFFF"/>
              <rect y="6.15" width="24" height="1.23" fill="#FFFFFF"/>
              <rect y="8.61" width="24" height="1.23" fill="#FFFFFF"/>
              <rect y="11.07" width="24" height="1.23" fill="#FFFFFF"/>
              <rect y="13.54" width="24" height="1.23" fill="#FFFFFF"/>
              {/* Blue canton */}
              <rect width="9.6" height="8.31" fill="#3C3B6E"/>
              {/* Stars */}
              <g fill="#FFFFFF" fontSize="0.8">
                <text x="1.2" y="2.4">★</text>
                <text x="2.4" y="2.4">★</text>
                <text x="3.6" y="2.4">★</text>
                <text x="4.8" y="2.4">★</text>
                <text x="6" y="2.4">★</text>
                <text x="1.8" y="3.6">★</text>
                <text x="3" y="3.6">★</text>
                <text x="4.2" y="3.6">★</text>
                <text x="5.4" y="3.6">★</text>
                <text x="6.6" y="3.6">★</text>
                <text x="1.2" y="4.8">★</text>
                <text x="2.4" y="4.8">★</text>
                <text x="3.6" y="4.8">★</text>
                <text x="4.8" y="4.8">★</text>
                <text x="6" y="4.8">★</text>
              </g>
            </>
          )}
          {countryCode === 'DE' && (
            <>
              <rect width="24" height="5.33" fill="#000000"/>
              <rect y="5.33" width="24" height="5.33" fill="#DD0000"/>
              <rect y="10.67" width="24" height="5.33" fill="#FFCE00"/>
            </>
          )}
          {countryCode === 'FR' && (
            <>
              <rect width="8" height="16" fill="#002395"/>
              <rect x="8" width="8" height="16" fill="#FFFFFF"/>
              <rect x="16" width="8" height="16" fill="#ED2939"/>
            </>
          )}
          {countryCode === 'VN' && (
            <>
              <rect width="24" height="16" fill="#DA020E"/>
              <polygon points="12,3 13.2,6.5 17,6.5 14.4,8.5 15.6,12 12,10 8.4,12 9.6,8.5 7,6.5 10.8,6.5" fill="#FFCD00"/>
            </>
          )}
          {countryCode === 'ES' && (
            <>
              <rect width="24" height="4" fill="#C60B1E"/>
              <rect y="4" width="24" height="8" fill="#FFC400"/>
              <rect y="12" width="24" height="4" fill="#C60B1E"/>
            </>
          )}
        </svg>
      </div>
    );
  };

  const languages = [
    { code: 'en', name: 'English', native: 'English', flagCode: 'US' },
    { code: 'de', name: 'Deutsch', native: 'Deutsch', flagCode: 'DE' },
    { code: 'fr', name: 'Français', native: 'Français', flagCode: 'FR' },
    { code: 'vi', name: 'Tiếng Việt', native: 'Tiếng Việt', flagCode: 'VN' },
    { code: 'es', name: 'Español', native: 'Español', flagCode: 'ES' }
  ];

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
    setCurrentLang(value);
    
    // Update HTML lang attribute
    document.documentElement.lang = value;
    
    // Update meta tags for SEO
    const metaLang = document.querySelector('meta[name="language"]');
    if (metaLang) {
      const languageNames = {
        'en': 'English',
        'de': 'German', 
        'fr': 'French',
        'vi': 'Vietnamese',
        'es': 'Spanish'
      };
      metaLang.setAttribute('content', languageNames[value as keyof typeof languageNames] || 'English');
    }
    
    // Update URL to reflect language change (only when user explicitly changes language)
    const currentPath = location;
    const pathParts = currentPath.split('/').filter(Boolean);
    const currentPathLang = pathParts[0];
    
    if (value === 'en') {
      // If switching to English, remove language prefix
      if (currentPathLang && ['de', 'fr', 'vi', 'es'].includes(currentPathLang)) {
        const pathWithoutLang = pathParts.slice(1).join('/');
        const newPath = pathWithoutLang ? `/${pathWithoutLang}` : '/';
        setLocation(newPath);
      }
    } else {
      // If switching to non-English, add language prefix
      if (currentPathLang && ['en', 'de', 'fr', 'vi', 'es'].includes(currentPathLang)) {
        // Current path already has a language prefix
        const pathWithoutLang = pathParts.slice(1).join('/');
        const newPath = `/${value}${pathWithoutLang ? `/${pathWithoutLang}` : ''}`;
        setLocation(newPath);
      } else {
        // Current path has no language prefix, add one
        const newPath = `/${value}${currentPath === '/' ? '' : currentPath}`;
        setLocation(newPath);
      }
    }
  };



  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-48 h-10 bg-white border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 flex items-center justify-between px-3"
        aria-label="Select language"
      >
        <div className="flex items-center space-x-2">
          <FlagIcon 
            countryCode={languages.find(l => l.code === currentLang)?.flagCode || 'US'} 
            className="w-5 h-3.5"
          />
          <span className="font-medium text-gray-700 whitespace-nowrap">
            {languages.find(l => l.code === currentLang)?.name || 'English'}
          </span>
        </div>
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-1 w-48 z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                handleLanguageChange(lang.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center space-x-2 py-2.5 px-3 hover:bg-blue-50 cursor-pointer rounded-lg transition-colors duration-150 ${
                lang.code === currentLang ? 'bg-blue-100' : ''
              }`}
            >
              <FlagIcon 
                countryCode={lang.flagCode} 
                className="w-5 h-3.5 flex-shrink-0"
              />
              <span className="font-medium text-gray-700 whitespace-nowrap text-left">
                {lang.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
